<script lang="ts">
  import EyeCloseIcon from '@/components/icons/eye-close.svelte'
  import EyeIcon from '@/components/icons/eye.svelte'
  import { getField } from '@/context/form'
  import {
    logComponentEvent,
    logTextEditEvent,
  } from '@/lib/native-messaging/log-event'
  import { Toast, showToast } from '@/lib/native-messaging/toast'
  import { clsx } from 'clsx'
  import { onDestroy } from 'svelte'
  import { _ } from 'svelte-i18n'
  import type { HTMLInputAttributes } from 'svelte/elements'

  const TEXT_INPUT_TYPES = ['text', 'email', 'password']

  interface $$Props extends HTMLInputAttributes {
    name: string
    type?: HTMLInputAttributes['type']
    maxlength?: number
    note?: string
    dataFieldName?: string
    of?: string
    defaultValue?: string
  }

  export let name: string
  export let type: HTMLInputAttributes['type'] = 'text'
  export let maxlength: number | null = null
  export let note = ''
  export let dataFieldName = ''
  export let of = ''
  export let defaultValue = ''

  const { error, ...field } = getField({ of, name, t: $_ })

  if (defaultValue) {
    field.touch()
  }

  onDestroy(() => {
    field.remove()
  })

  let inputVal = defaultValue
  let isFocused = false
  let inputElement: HTMLInputElement
  let isPasswordVisible = type !== 'password'
  $: resolvedType =
    type === 'password' ? (isPasswordVisible ? 'text' : 'password') : type

  function handleEyeBtnClick() {
    logComponentEvent({
      type: 'btn',
      appAction: 'click',
      componentName: isPasswordVisible ? 'hide_password' : 'show_password',
    })
    isPasswordVisible = !isPasswordVisible
    inputElement.focus()
  }

  function handleFocus() {
    if (TEXT_INPUT_TYPES.includes(type as string)) {
      logTextEditEvent('start', dataFieldName)
    }
    isFocused = true
    field.touch()
  }

  function handleBlur() {
    if (TEXT_INPUT_TYPES.includes(type as string)) {
      logTextEditEvent('end', dataFieldName)
    }
    isFocused = false
    field.validate(inputVal)
  }

  function handleInput(
    event: Event & {
      currentTarget: EventTarget & HTMLInputElement
    },
  ) {
    if (maxlength !== null) {
      if (event.currentTarget.value.length > maxlength) {
        showToast(Toast.UsernameTooLong)
      }
      inputVal = event.currentTarget.value.slice(0, maxlength)
      event.currentTarget.value = inputVal
    } else {
      inputVal = event.currentTarget.value
    }
  }
</script>

<div class="relative">
  <input
    {...$$restProps}
    data-1p-ignore
    bind:this={inputElement}
    class={clsx(
      'h-[45px] w-full appearance-none rounded-[16px] bg-element-input px-[12px] text-left text-content-primary outline-[1px] outline-offset-[-1px] text-body-md placeholder:text-content-placeholder',
      $error ? 'outline outline-warning' : 'outline-secondary focus:outline',
    )}
    {name}
    type={resolvedType}
    value={inputVal}
    on:input={handleInput}
    on:focus={handleFocus}
    on:blur={handleBlur}
  />
  {#if type === 'password'}
    <button
      type="button"
      class="absolute bottom-0 right-2 top-[12.5px] h-5 w-5"
      on:click={handleEyeBtnClick}
    >
      {#if isPasswordVisible}
        <EyeCloseIcon />
      {:else}
        <EyeIcon />
      {/if}
    </button>
  {/if}
  {#if $error}
    <p class="mt-[8px] text-right text-warning text-label-md">
      {$error}
    </p>
  {/if}
  {#if maxlength !== null && isFocused}
    <p class="mt-[8px] text-right text-content-tertiary text-label-md">
      {inputVal.length}/{maxlength}
    </p>
  {/if}
  {#if note && isFocused}
    <p class="mt-[8px] text-right text-content-tertiary text-label-md">
      {note}
    </p>
  {/if}
</div>
